import React from 'react';
import {
  CheckCircleIcon,
  ChevronDoubleRightIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  ChatBubbleBottomCenterIcon,
  CalendarIcon,
  UserGroupIcon,
  CogIcon,
  ChartBarIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

export default function TaskCalendarTodayHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <CalendarIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Daily Task Timeline</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Real-time task management interface showing today's scheduled tasks,
            progress tracking, and interactive communication tools.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Daily Task Timeline Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <ClockIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Real-Time Tracking</h3>
                <p className="text-gray-600">
                  Live task progress with automatic time tracking and status updates.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircleIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Task Completion</h3>
                <p className="text-gray-600">
                  Mark tasks as complete with outcome recording and progress validation.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <ChatBubbleBottomCenterIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Communication</h3>
                <p className="text-gray-600">
                  Built-in messaging system for task-related discussions and support.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Issue Management</h3>
                <p className="text-gray-600">
                  Report problems, request help, and track resolution progress.
                </p>
              </div>
            </div>

            {/* Static Timeline Demo */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">Daily Task Timeline Interface</h3>

              {/* Static Header */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden mb-8">
                <div className="bg-white shadow p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between md:gap-8 space-y-3 md:space-y-0">
                    <h1 className="text-lg md:text-xl font-bold text-indigo-600">
                      Monday, October 14, 2025
                    </h1>
                    <div className="flex flex-col md:flex-row md:items-center md:gap-6 w-full">
                      <h3 className="text-xs md:text-base text-white p-2 rounded-md bg-indigo-600 inline-block md:mb-0 mb-2 md:mr-0 mr-2">
                        John Smith
                      </h3>
                      <span className="text-xs md:text-base text-gray-600 md:ml-0 ml-2">
                        Current Task:{" "}
                        <span className="font-semibold text-indigo-600">
                          Customer Service Call
                        </span>
                      </span>
                      <div className="flex items-center p-2 bg-violet-600 rounded-lg w-min md:ml-auto">
                        <ClockIcon className="h-5 w-5 text-white" aria-hidden="true" />
                        <span className="text-lg text-white ml-1 font-bebas">
                          10:30
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Task Cards Demo */}
                <div className="p-6 space-y-4">
                  {/* Upcoming Task */}
                  <div className="bg-indigo-600 p-4 rounded-md shadow-md text-white relative">
                    <div className="flex justify-between items-center">
                      <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white text-indigo-600 font-semibold text-sm md:text-base">
                        1
                      </div>
                      <p className="text-xs p-1 md:p-2 bg-indigo-600 inline-block font-bebas rounded-md">Sales Process</p>
                      <button className="bg-white p-2 rounded-full">
                        <ChatBubbleBottomCenterIcon className="w-6 h-6 text-indigo-600" />
                      </button>
                      <button className="focus:outline-none">
                        <ChevronDoubleRightIcon className="w-8 h-8 text-white" />
                      </button>
                    </div>
                    <div><ClockIcon className="h-6 w-6 text-white" aria-hidden="true" /></div>
                    <p className="text-sm mt-2">Sales Process</p>
                    <p className="font-semibold">Customer Service Call</p>
                    <p className="text-sm mt-2">
                      <time>09:00</time> -{" "}
                      <time>10:00</time>
                    </p>
                    <p className="text-sm mt-2">Handle incoming customer inquiries and provide support</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <button className="bg-indigo-600 px-3 py-2 text-white rounded-md hover:bg-indigo-500">
                        Ask For Help?
                      </button>
                      <button className="bg-white px-3 py-2 rounded-md">
                        <img src="tree.png" alt="Knowledge Tree" className="w-6 h-6" />
                      </button>
                      <button className="bg-white px-2 py-1 md:px-3 md:py-2 text-xs md:text-sm text-indigo-600 rounded-md hover:bg-indigo-100">
                        View Documents
                      </button>
                    </div>
                  </div>

                  {/* Current Task */}
                  <div className="bg-emerald-500 p-4 rounded-md shadow-md text-white relative">
                    <div className="flex justify-between items-center">
                      <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white text-emerald-600 font-semibold text-sm md:text-base">
                        2
                      </div>
                      <p className="text-xs p-1 md:p-2 bg-emerald-600 inline-block font-bebas rounded-md">Inventory</p>
                      <button className="focus:outline-none">
                        <ChevronDoubleRightIcon className="w-8 h-8 text-white" />
                      </button>
                    </div>
                    <div><ExclamationCircleIcon className="h-6 w-6 text-white" aria-hidden="true" /></div>
                    <p className="text-sm mt-2">Inventory Management</p>
                    <p className="font-semibold">Stock Count</p>
                    <p className="text-sm mt-2">
                      <time>10:00</time> -{" "}
                      <time>11:00</time>
                    </p>
                    <p className="text-sm mt-2">Perform inventory count and update stock levels</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <button className="bg-indigo-600 px-3 py-2 text-white rounded-md hover:bg-indigo-500">
                        Ask For Help?
                      </button>
                      <button className="bg-white px-3 py-2 rounded-md">
                        <img src="tree.png" alt="Knowledge Tree" className="w-6 h-6" />
                      </button>
                      <button className="bg-white px-2 py-1 md:px-3 md:py-2 text-xs md:text-sm text-emerald-600 rounded-md hover:bg-emerald-100">
                        View Documents
                      </button>
                    </div>
                  </div>

                  {/* Break */}
                  <div className="bg-yellow-400 p-4 rounded-md shadow-md text-gray-800 relative">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm md:text-base">Break Time</h3>
                        <p className="text-xs md:text-sm">
                          11:00 - 11:15
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Completed Task */}
                  <div className="bg-red-500 p-4 rounded-md shadow-md text-white relative">
                    <div className="flex justify-between items-center">
                      <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white text-red-600 font-semibold text-sm md:text-base">
                        3
                      </div>
                      <p className="text-xs p-1 md:p-2 bg-red-600 inline-block font-bebas rounded-md">Cash Handling</p>
                      <button className="focus:outline-none">
                        <ChevronDoubleRightIcon className="w-8 h-8 text-white" />
                      </button>
                    </div>
                    <div><ExclamationCircleIcon className="h-6 w-6 text-white animate-pulse" aria-hidden="true" /></div>
                    <p className="text-sm mt-2">Cash Handling Process</p>
                    <p className="font-semibold">Cash Register Balancing</p>
                    <p className="text-sm mt-2">
                      <time>11:15</time> -{" "}
                      <time>12:15</time>
                    </p>
                    <p className="text-sm mt-2">Balance cash register and prepare deposit</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <button className="bg-emerald-600 px-3 py-2 text-white rounded-md hover:bg-emerald-500">
                        Completed Task
                      </button>
                      <button className="bg-yellow-500 px-3 py-2 text-white rounded-md hover:bg-yellow-600">
                        Raise Issue
                      </button>
                      <button className="bg-white px-2 py-1 md:px-3 md:py-2 text-xs md:text-sm text-red-600 rounded-md hover:bg-red-100">
                        View Documents
                      </button>
                    </div>
                  </div>

                  {/* Completed Task with Outcome */}
                  <div className="bg-red-500 p-4 rounded-md shadow-md text-white relative">
                    <div className="flex justify-between items-center">
                      <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white text-red-600 font-semibold text-sm md:text-base">
                        4
                      </div>
                      <p className="text-xs p-1 md:p-2 bg-red-600 inline-block font-bebas rounded-md">Customer Service</p>
                      <button className="focus:outline-none">
                        <ChevronDoubleRightIcon className="w-8 h-8 text-white" />
                      </button>
                    </div>
                    <div><CheckCircleIcon className="h-12 w-12 text-emerald-300 animate-bounce" aria-hidden="true" /></div>
                    <p className="text-sm mt-2">Customer Service Process</p>
                    <p className="font-semibold">Handle Customer Complaint</p>
                    <p className="text-sm mt-2">
                      <time>13:00</time> -{" "}
                      <time>14:00</time>
                    </p>
                    <p className="text-sm mt-2">Address customer concerns and resolve issues</p>
                    <p className="text-sm mt-2 p-2 font-bold bg-white text-red-500 rounded-md inline-block">
                      Task Outcome: Customer satisfied, issue resolved successfully
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <button className="bg-white px-2 py-1 md:px-3 md:py-2 text-xs md:text-sm text-red-600 rounded-md hover:bg-red-100">
                        View Documents
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Interface Explanations */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">1</span>
                    </div>
                    <h4 className="text-lg font-bold text-blue-900">Header Information</h4>
                  </div>
                  <p className="text-blue-700 text-sm leading-relaxed">
                    Displays current date, user name, active task, and live clock. Shows real-time
                    status of what task should be worked on currently.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">2</span>
                    </div>
                    <h4 className="text-lg font-bold text-indigo-900">Upcoming Tasks</h4>
                  </div>
                  <p className="text-indigo-700 text-sm leading-relaxed">
                    Blue cards show future tasks. Include step numbers, process names, time slots,
                    and action buttons for help requests and document access.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">3</span>
                    </div>
                    <h4 className="text-lg font-bold text-emerald-900">Current Tasks</h4>
                  </div>
                  <p className="text-emerald-700 text-sm leading-relaxed">
                    Green cards indicate active tasks that should be worked on now. Show warning
                    icons and provide immediate access to help and resources.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">4</span>
                    </div>
                    <h4 className="text-lg font-bold text-yellow-900">Break Periods</h4>
                  </div>
                  <p className="text-yellow-700 text-sm leading-relaxed">
                    Yellow cards indicate scheduled break times. Show break duration and prevent
                    work activities during designated rest periods.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-red-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">5</span>
                    </div>
                    <h4 className="text-lg font-bold text-red-900">Past Tasks</h4>
                  </div>
                  <p className="text-red-700 text-sm leading-relaxed">
                    Red cards show completed or overdue tasks. Provide completion buttons,
                    issue reporting, and show recorded outcomes when available.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">6</span>
                    </div>
                    <h4 className="text-lg font-bold text-purple-900">Task Actions</h4>
                  </div>
                  <p className="text-purple-700 text-sm leading-relaxed">
                    Context-sensitive buttons for task management: complete tasks, raise issues,
                    access knowledge base, view documents, and communicate.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-teal-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">7</span>
                    </div>
                    <h4 className="text-lg font-bold text-teal-900">Communication</h4>
                  </div>
                  <p className="text-teal-700 text-sm leading-relaxed">
                    Chat bubble icons indicate unread messages. Click to open communication
                    panel for task-related discussions and support.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">8</span>
                    </div>
                    <h4 className="text-lg font-bold text-pink-900">Task Details</h4>
                  </div>
                  <p className="text-pink-700 text-sm leading-relaxed">
                    Arrow buttons expand to show detailed substeps and procedures for each task.
                    Provides step-by-step guidance and checklists.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works - Step by Step */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <ArrowRightIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">How Daily Task Timeline Works</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">View Today's Schedule</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    The timeline automatically loads and displays all tasks scheduled for the current day,
                    sorted chronologically by start time.
                  </p>

                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-blue-900 mb-3">Automatic Loading</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">Fetches tasks from server on page load</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">Sorts tasks by scheduled time</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">Highlights current active task</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-blue-700">Shows break periods separately</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h5 className="font-semibold text-blue-900 mb-2">Real-Time Updates</h5>
                        <div className="text-sm text-blue-700">
                          <div className="bg-blue-50 p-2 rounded mb-2">
                            <strong>Live Clock:</strong> Updates every second
                          </div>
                          <div className="bg-blue-50 p-2 rounded mb-2">
                            <strong>Task Status:</strong> Changes color based on time
                          </div>
                          <div className="text-xs text-gray-600">
                            The interface automatically scrolls to keep the current task visible.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Work Through Tasks</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Monitor task progress with color-coded status indicators and access resources
                    as needed throughout the day.
                  </p>

                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-indigo-900 mb-3">Task States</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Blue: Upcoming tasks</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Green: Current active task</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Red: Past/overdue tasks</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-indigo-700">Yellow: Break periods</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-indigo-200">
                        <h5 className="font-semibold text-indigo-900 mb-2">Resource Access</h5>
                        <div className="space-y-2 text-sm text-indigo-700">
                          <div className="flex items-center">
                            <QuestionMarkCircleIcon className="h-4 w-4 text-indigo-500 mr-2" />
                            Ask for Help - Request assistance
                          </div>
                          <div className="flex items-center">
                            <img src="tree.png" alt="" className="w-4 h-4 mr-2" />
                            Knowledge Tree - Access guides
                          </div>
                          <div className="flex items-center">
                            <ClipboardDocumentCheckIcon className="h-4 w-4 text-indigo-500 mr-2" />
                            View Documents - Process docs
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-indigo-200">
                        <h5 className="font-semibold text-indigo-900 mb-2">Task Details</h5>
                        <div className="text-sm text-indigo-700">
                          <div className="bg-indigo-50 p-2 rounded mb-2">
                            <strong>Step Numbers:</strong> Process sequence
                          </div>
                          <div className="bg-indigo-50 p-2 rounded mb-2">
                            <strong>Time Slots:</strong> Scheduled duration
                          </div>
                          <div className="bg-indigo-50 p-2 rounded">
                            <strong>Descriptions:</strong> Task requirements
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Complete and Report Tasks</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Mark tasks as completed with outcome descriptions, or report issues that need
                    manager attention and support.
                  </p>

                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-purple-900 mb-3">Task Completion</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Click "Completed Task" for finished work</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Enter outcome description in modal</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Task status changes with checkmark icon</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Outcome displayed on task card</span>
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-purple-900 mb-3">Issue Reporting</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Click "Raise Issue" for problems encountered</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Describe the struggle and attempted solutions</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Specify which substep caused the issue</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-purple-700">Choose who to ask for help (manager/team)</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-pink-500 to-red-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Communicate and Collaborate</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Use built-in messaging for task-related discussions and receive help responses
                    from managers and team members.
                  </p>

                  <div className="bg-gradient-to-r from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-pink-900 mb-3">Messaging System</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Chat bubbles show unread messages</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Click to open full conversation</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Real-time message updates</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Task-specific communication threads</span>
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-pink-900 mb-3">Help Responses</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Manager responses to reported issues</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Rate responses with star ratings</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Track resolution progress</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-pink-700">Historical help conversation logs</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-pink-200">
                        <h5 className="font-semibold text-pink-900 mb-2">Message Features</h5>
                        <div className="space-y-2 text-sm text-pink-700">
                          <div className="bg-pink-50 p-2 rounded">
                            <strong>Real-time:</strong> Instant message delivery
                          </div>
                          <div className="bg-pink-50 p-2 rounded">
                            <strong>Contextual:</strong> Task-specific conversations
                          </div>
                          <div className="bg-pink-50 p-2 rounded">
                            <strong>Collaborative:</strong> Team and manager support
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Features */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-8 py-6">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Technical Features & Capabilities</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <ClockIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Real-Time Updates</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Live clock updates every second</li>
                  <li>• Automatic task status changes</li>
                  <li>• Dynamic progress indicators</li>
                  <li>• Real-time message notifications</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <CheckCircleIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Task State Management</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Color-coded task status indicators</li>
                  <li>• Automatic state transitions</li>
                  <li>• Completion validation</li>
                  <li>• Outcome recording system</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                    <ChatBubbleBottomCenterIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Communication Hub</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Integrated messaging system</li>
                  <li>• Task-specific conversations</li>
                  <li>• Help request routing</li>
                  <li>• Response rating system</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-red-600 rounded-lg flex items-center justify-center mr-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Issue Tracking</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Structured issue reporting</li>
                  <li>• Substep-level problem tracking</li>
                  <li>• Help request escalation</li>
                  <li>• Resolution workflow</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-600 rounded-lg flex items-center justify-center mr-3">
                    <ChevronDoubleRightIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Interactive Details</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Expandable task substeps</li>
                  <li>• Document access integration</li>
                  <li>• Knowledge base linking</li>
                  <li>• Process documentation</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-600 rounded-lg flex items-center justify-center mr-3">
                    <EyeIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Progress Monitoring</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Visual task completion status</li>
                  <li>• Time-based progress tracking</li>
                  <li>• Break compliance monitoring</li>
                  <li>• Performance analytics</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-orange-600 px-8 py-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Troubleshooting & Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <h3 className="text-lg font-bold text-red-900 mb-3">Common Issues</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">No Tasks Displayed</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Check if tasks are scheduled for today</li>
                        <li>• Verify internet connection for data loading</li>
                        <li>• Refresh the page to reload task data</li>
                        <li>• Contact manager if tasks should be assigned</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">Messages Not Sending</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Ensure stable internet connection</li>
                        <li>• Check message content is not empty</li>
                        <li>• Verify task selection for message context</li>
                        <li>• Try sending again after a moment</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">Task Completion Issues</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Ensure outcome description is provided</li>
                        <li>• Check for server response confirmation</li>
                        <li>• Verify task status changes after completion</li>
                        <li>• Refresh page if changes don't appear</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                  <h3 className="text-lg font-bold text-yellow-900 mb-3">Break Compliance</h3>
                  <ul className="space-y-2 text-sm text-yellow-700">
                    <li>• Breaks are mandatory for shifts over 6 hours</li>
                    <li>• Break times are predefined in shift schedules</li>
                    <li>• Work activities are blocked during break periods</li>
                    <li>• Break compliance is tracked and reported</li>
                    <li>• Early break completion may be allowed with approval</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Best Practices</h3>
                  <ul className="space-y-3 text-sm text-blue-700">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Complete tasks promptly when they become active</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Use the "Ask for Help" feature early when encountering difficulties</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Take scheduled breaks to maintain productivity and compliance</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Provide detailed outcome descriptions when completing tasks</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Check messages regularly for manager responses and updates</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Rate help responses to improve future assistance quality</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-lg font-bold text-green-900 mb-3">Performance Tips</h3>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Keep the timeline visible throughout the workday</li>
                    <li>• Monitor the live clock for task transition timing</li>
                    <li>• Use keyboard shortcuts for faster task completion</li>
                    <li>• Review substep details before starting complex tasks</li>
                    <li>• Archive completed tasks to reduce visual clutter</li>
                    <li>• Set notifications for important task deadlines</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}